/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * useSurfaceStyles Hook
 *
 * Provides memoized semantic surface styles for components.
 * Reduces repetitive useMemo calls and provides a clean API for semantic styling.
 *
 * @example
 * ```tsx
 * const styles = useSurfaceStyles('primary');
 *
 * // Use badge props
 * <Badge {...styles.badgeProps}>Label</Badge>
 *
 * // Use hover handlers
 * <div {...styles.hoverProps}>Content</div>
 * ```
 */

import { useMemo } from "react";

import {
  createSurfaceStyles,
  type SurfaceStyleProps,
} from "@/lib/utils/design/semantic-surface-styles";
import type { SemanticIntent } from "./use-design-tokens";
import { useSemanticSurfaces } from "./use-semantic-surfaces";

export interface UseSurfaceStylesResult {
  /** Props for Badge component */
  badgeProps: SurfaceStyleProps["badgeProps"];
  /** Props for Chip component */
  chipProps: SurfaceStyleProps["chipProps"];
  /** Props for hoverable elements */
  hoverProps: SurfaceStyleProps["hoverProps"];
  /** Style object for cards */
  cardStyle: SurfaceStyleProps["cardStyle"];
  /** Style object for text */
  textStyle: SurfaceStyleProps["textStyle"];
  /** Style object for strong text */
  textStrongStyle: SurfaceStyleProps["textStrongStyle"];
  /** Style object for muted text */
  textMutedStyle: SurfaceStyleProps["textMutedStyle"];
  /** Style object for icons */
  iconStyle: SurfaceStyleProps["iconStyle"];
  /** Style object for borders */
  borderStyle: SurfaceStyleProps["borderStyle"];
  /** Hover background color */
  hoverBackground: SurfaceStyleProps["hoverBackground"];
  /** Hover text color */
  hoverText: SurfaceStyleProps["hoverText"];
  /** Hover border color */
  hoverBorder: SurfaceStyleProps["hoverBorder"];
  /** Raw surface colors */
  surface: ReturnType<typeof useSemanticSurfaces>["neutral"];
  /** Raw interaction colors */
  interaction: ReturnType<typeof useSemanticSurfaces>["neutralInteraction"];
}

/**
 * Returns memoized semantic surface styles for a given intent.
 *
 * @param intent - Semantic intent (e.g., 'primary', 'success', 'warning')
 * @returns Memoized surface style props and utilities
 */
export function useSurfaceStyles(
  intent: SemanticIntent = "neutral",
): UseSurfaceStylesResult {
  const surfaces = useSemanticSurfaces();

  return useMemo(() => {
    const surface = surfaces.getSurface(intent);
    const interaction = surfaces.getInteraction(intent);
    const styles = createSurfaceStyles(surface, interaction);

    return {
      ...styles,
      surface,
      interaction,
    };
  }, [surfaces, intent]);
}

/**
 * Returns memoized semantic surface styles for multiple intents.
 * Useful when you need styles for multiple semantic surfaces in one component.
 *
 * @param intents - Array of semantic intents
 * @returns Record mapping each intent to its surface styles
 */
export function useMultipleSurfaceStyles(
  intents: SemanticIntent[],
): Record<SemanticIntent, UseSurfaceStylesResult> {
  const surfaces = useSemanticSurfaces();

  return useMemo(() => {
    const result = {} as Record<SemanticIntent, UseSurfaceStylesResult>;

    for (const intent of intents) {
      const surface = surfaces.getSurface(intent);
      const interaction = surfaces.getInteraction(intent);
      const styles = createSurfaceStyles(surface, interaction);

      result[intent] = {
        ...styles,
        surface,
        interaction,
      };
    }

    return result;
  }, [surfaces, intents]);
}
